import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function AdminTourForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    price: '',
    duration: '',
    spots_available: 20,
    images: [],
    short_description: '',
    long_description: '',
    itinerary: [{ time: '', activity: '' }],
    includes: [''],
    not_includes: [''],
    what_to_bring: [''],
    what_to_wear: [''],
    is_published: true
  });

  const fetchTour = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.getTours();
      const tour = response.data.find(t => t.id === id);
      if (tour) {
        setFormData({
          name: tour.name || '',
          slug: tour.slug || '',
          price: String(tour.price || ''),
          duration: tour.duration || '',
          spots_available: tour.spots_available || 20,
          images: tour.images || [],
          short_description: tour.short_description || '',
          long_description: tour.long_description || '',
          itinerary: tour.itinerary?.length ? tour.itinerary : [{ time: '', activity: '' }],
          includes: tour.includes?.length ? tour.includes : [''],
          not_includes: tour.not_includes?.length ? tour.not_includes : [''],
          what_to_bring: tour.what_to_bring?.length ? tour.what_to_bring : [''],
          what_to_wear: tour.what_to_wear?.length ? tour.what_to_wear : [''],
          is_published: tour.is_published !== false
        });
      }
    } catch (error) {
      toast.error('Error al cargar tour');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      fetchTour();
    }
  }, [isEdit, fetchTour]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'name' && !isEdit) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };

  const handleItineraryChange = (index, key, value) => {
    setFormData(prev => {
      const itinerary = [...prev.itinerary];
      itinerary[index] = { ...itinerary[index], [key]: value };
      return { ...prev, itinerary };
    });
  };

  const addArrayItem = (field, defaultValue = '') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Image upload handling
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newImages = [...formData.images];

    for (const file of files) {
      try {
        const result = await adminApi.uploadMedia(file, 'tours', file.name);
        if (result.data?.url) {
          newImages.push(result.data.url);
          toast.success(`${file.name} subida`);
        }
      } catch (error) {
        toast.error(`Error subiendo ${file.name}`);
      }
    }

    setFormData(prev => ({ ...prev, images: newImages }));
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const cleanData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        spots_available: parseInt(formData.spots_available) || 20,
        images: formData.images.filter(Boolean),
        itinerary: formData.itinerary.filter(i => i.time && i.activity),
        includes: formData.includes.filter(i => i.trim()),
        not_includes: formData.not_includes.filter(i => i.trim()),
        what_to_bring: formData.what_to_bring.filter(i => i.trim()),
        what_to_wear: formData.what_to_wear.filter(i => i.trim()),
      };

      if (isEdit) {
        await adminApi.updateTour(id, cleanData);
        toast.success('Tour actualizado');
      } else {
        await adminApi.createTour(cleanData);
        toast.success('Tour creado');
      }
      navigate('/admin/tours');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button type="button" variant="ghost" size="icon" onClick={() => navigate('/admin/tours')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-serif text-stone-900">
              {isEdit ? 'Editar Tour' : 'Nuevo Tour'}
            </h1>
            <p className="text-stone-500">
              {isEdit ? 'Modifica los detalles del tour' : 'Crea una nueva excursión'}
            </p>
          </div>
        </div>
        <Button type="submit" disabled={saving} className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? 'Guardando...' : 'Guardar Tour'}
        </Button>
      </div>

      {/* Información Básica */}
      <Card>
        <CardHeader>
          <CardTitle>Información Básica</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nombre del Tour *</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ej: Isla Saona"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>URL (slug)</Label>
              <Input
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="isla-saona"
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Precio (USD) *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="79"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Duración *</Label>
              <Input
                value={formData.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                placeholder="8-10 horas"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Cupos disponibles</Label>
              <Input
                type="number"
                value={formData.spots_available}
                onChange={(e) => handleChange('spots_available', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch
              checked={formData.is_published}
              onCheckedChange={(checked) => handleChange('is_published', checked)}
            />
            <Label>Tour publicado (visible en el sitio)</Label>
          </div>
        </CardContent>
      </Card>

      {/* Imágenes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Imágenes del Tour
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-stone-500 mb-4">
            Sube las fotos del tour. La primera imagen será la principal.
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {/* Uploaded Images */}
            {formData.images.map((img, index) => (
              <div key={index} className="relative group aspect-square bg-stone-100 rounded-xl overflow-hidden">
                <img
                  src={getImageUrl(img)}
                  alt={`Imagen ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
                {index === 0 && (
                  <span className="absolute bottom-2 left-2 px-2 py-1 bg-primary text-white text-xs rounded-lg">
                    Principal
                  </span>
                )}
              </div>
            ))}
            
            {/* Upload Button */}
            <label className="aspect-square border-2 border-dashed border-stone-300 rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
              {uploading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-stone-400" />
                  <span className="text-sm text-stone-500 text-center px-2">
                    Subir fotos
                  </span>
                </>
              )}
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Descripción */}
      <Card>
        <CardHeader>
          <CardTitle>Descripción</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Descripción corta *</Label>
            <Input
              value={formData.short_description}
              onChange={(e) => handleChange('short_description', e.target.value)}
              placeholder="Breve descripción para las tarjetas"
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label>Descripción completa</Label>
            <Textarea
              value={formData.long_description}
              onChange={(e) => handleChange('long_description', e.target.value)}
              placeholder="Descripción detallada del tour..."
              rows={4}
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Itinerario */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Itinerario</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addArrayItem('itinerary', { time: '', activity: '' })}
          >
            <Plus className="w-4 h-4 mr-1" /> Agregar
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {formData.itinerary.map((item, index) => (
            <div key={index} className="flex gap-3 items-start">
              <Input
                value={item.time}
                onChange={(e) => handleItineraryChange(index, 'time', e.target.value)}
                placeholder="9:00 AM"
                className="w-28"
              />
              <Input
                value={item.activity}
                onChange={(e) => handleItineraryChange(index, 'activity', e.target.value)}
                placeholder="Actividad..."
                className="flex-1"
              />
              {formData.itinerary.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeArrayItem('itinerary', index)}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Incluye / No Incluye */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-green-600">Incluye</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('includes')}>
              <Plus className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {formData.includes.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => handleArrayChange('includes', index, e.target.value)}
                  placeholder="Transporte, almuerzo..."
                />
                {formData.includes.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('includes', index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-red-600">No Incluye</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('not_includes')}>
              <Plus className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {formData.not_includes.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => handleArrayChange('not_includes', index, e.target.value)}
                  placeholder="Propinas, bebidas extra..."
                />
                {formData.not_includes.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('not_includes', index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Qué Llevar / Qué Vestir */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Qué Llevar</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('what_to_bring')}>
              <Plus className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {formData.what_to_bring.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => handleArrayChange('what_to_bring', index, e.target.value)}
                  placeholder="Protector solar, cámara..."
                />
                {formData.what_to_bring.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('what_to_bring', index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Qué Vestir</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('what_to_wear')}>
              <Plus className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {formData.what_to_wear.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => handleArrayChange('what_to_wear', index, e.target.value)}
                  placeholder="Traje de baño, ropa ligera..."
                />
                {formData.what_to_wear.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('what_to_wear', index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-4 pt-4">
        <Button type="button" variant="outline" onClick={() => navigate('/admin/tours')}>
          Cancelar
        </Button>
        <Button type="submit" disabled={saving} className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? 'Guardando...' : 'Guardar Tour'}
        </Button>
      </div>
    </form>
  );
}
